import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["autoComplete", "passwordValue", "isHidden", "label", "passwordRequirementsValidation", "handleKeyUp", "handleOnBlur", "handleOnFocus"];
import { Visibility, VisibilityOff } from '@ori-ui/icons';
import { ButtonBase, styled, TextField, theme as themeValues } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import { useField } from 'formik';
import uniqueId from 'lodash.uniqueid';
import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LocalizationKeys } from '../../models/localizationKeys';
import { InputErrorIcon } from './InputErrorIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var PasswordInputRoot = styled('div')(function (_ref) {
  var theme = _ref.theme;
  return {
    marginBottom: theme.spacing(4),
    position: 'relative'
  };
});
var PasswordInputTextField = styled(TextField)(function () {
  return {
    height: 38,
    width: '100%'
  };
});
var PasswordInputInfo = styled('div')(function () {
  return {
    display: 'flex',
    position: 'absolute',
    right: 0,
    top: '50%'
  };
});
var inputLabels = {
  ConfirmNewPassword: LocalizationKeys.confirmNewPassword.id,
  NewPassword: LocalizationKeys.newPassword.id,
  Password: LocalizationKeys.password.id,
  TemporaryPassword: LocalizationKeys.temporaryPassword.id
};
var PasswordInputIcon = styled(ButtonBase)(function (_ref2) {
  var theme = _ref2.theme;
  return {
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(2),
    '& > svg': {
      fontSize: theme.typography.pxToRem(26)
    }
  };
});
export var PasswordInput = function PasswordInput(_ref3) {
  var autoComplete = _ref3.autoComplete,
      passwordValue = _ref3.passwordValue,
      isHidden = _ref3.isHidden,
      label = _ref3.label,
      passwordRequirementsValidation = _ref3.passwordRequirementsValidation,
      handleKeyUp = _ref3.handleKeyUp,
      handleOnBlur = _ref3.handleOnBlur,
      handleOnFocus = _ref3.handleOnFocus,
      props = _objectWithoutProperties(_ref3, _excluded);

  var generatedUniqueId = uniqueId('input-');

  var _useTranslation = useTranslation(),
      _useTranslation2 = _slicedToArray(_useTranslation, 1),
      translate = _useTranslation2[0];

  var _useField = useField(props),
      _useField2 = _slicedToArray(_useField, 3),
      field = _useField2[0],
      meta = _useField2[1],
      helpers = _useField2[2];

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      shouldShowPassword = _useState2[0],
      setShouldShowPassword = _useState2[1];

  var name = field.name,
      value = field.value,
      onBlur = field.onBlur,
      onChange = field.onChange;
  var setValue = helpers.setValue;

  var _useTestId = useTestId(),
      getTestId = _useTestId.getTestId;

  var isError = Boolean(meta.error && meta.touched);
  var fieldValue = passwordValue !== null && passwordValue !== void 0 ? passwordValue : value;
  var showErrorIcon = isError && !passwordRequirementsValidation;
  useEffect(function () {
    if (passwordValue && passwordValue !== value) {
      flushSync(function () {
        return setValue(passwordValue);
      });
    }
  }, [passwordValue, value, setValue]);
  var togglePasswordVisibility = useCallback(function () {
    setShouldShowPassword(function (prevState) {
      return !prevState;
    });
  }, [setShouldShowPassword]);
  var passwordInputLabel = label ? translate(label) : "".concat(translate(inputLabels[props.name]), "*");

  var handleOnBlurCombined = function handleOnBlurCombined(e) {
    onBlur(e);

    if (handleOnBlur) {
      handleOnBlur();
    }
  };

  return /*#__PURE__*/_jsxs(PasswordInputRoot, {
    hidden: isHidden,
    "data-testid": getTestId('password-wrapper'),
    children: [/*#__PURE__*/_jsx(PasswordInputTextField, {
      InputLabelProps: {
        shrink: true
      },
      error: isError,
      id: generatedUniqueId,
      inputProps: {
        'data-testid': getTestId("input-".concat(name.toLowerCase())),
        autoComplete: autoComplete
      },
      label: passwordInputLabel,
      name: name,
      type: shouldShowPassword ? 'text' : 'password',
      value: fieldValue,
      variant: "standard",
      sx: {
        '& .MuiInputBase-root::before': {
          borderBottom: "1px solid ".concat(themeValues.palette.grey[300])
        }
      },
      "data-testid": getTestId('password-input'),
      onBlur: handleOnBlurCombined,
      onChange: onChange,
      onKeyUp: handleKeyUp,
      onFocus: handleOnFocus
    }), /*#__PURE__*/_jsxs(PasswordInputInfo, {
      "data-testid": getTestId('password-info'),
      children: [showErrorIcon && /*#__PURE__*/_jsx(InputErrorIcon, {
        error: String(meta.error)
      }), /*#__PURE__*/_jsx(PasswordInputIcon, {
        disableRipple: true,
        onClick: togglePasswordVisibility,
        children: shouldShowPassword ? /*#__PURE__*/_jsx(VisibilityOff, {}) : /*#__PURE__*/_jsx(Visibility, {})
      })]
    })]
  });
};